import * as Sentry from "@sentry/react";

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;

function scrubPreferenceCenterUrl(value: unknown): unknown {
  if (typeof value !== "string" || !value.includes("/email-preferences/"))
    return value;
  try {
    const url = new URL(value, window.location.origin);
    return `${url.origin}${url.pathname}`;
  } catch {
    return value.split("?")[0];
  }
}

export function initSentry() {
  // Signed preference URLs carry the recipient email. Keep this public surface
  // entirely out of Sentry (including Replay's initial navigation capture).
  if (window.location.pathname.startsWith("/email-preferences/")) return;
  // Only initialize if DSN is configured
  if (!SENTRY_DSN) {
    console.log("[Sentry] No DSN configured, skipping initialization");
    return;
  }

  const environment = import.meta.env.MODE || "development";

  Sentry.init({
    dsn: SENTRY_DSN,
    environment,

    // Performance Monitoring
    tracesSampleRate: environment === "production" ? 0.1 : 1.0,

    // Session Replay - only in production
    replaysSessionSampleRate: environment === "production" ? 0.1 : 0,
    replaysOnErrorSampleRate: environment === "production" ? 1.0 : 0,

    // Filter out noisy errors
    ignoreErrors: [
      // Network errors that are often transient
      "Network request failed",
      "Failed to fetch",
      "Load failed",
      // Browser extensions
      "ResizeObserver loop",
      // User-initiated navigation
      "AbortError",
    ],

    // Add user context when available
    beforeSend(event) {
      // Remove sensitive data
      if (event.request?.headers) {
        delete event.request.headers["Authorization"];
        delete event.request.headers["Cookie"];
      }
      if (event.request?.url)
        event.request.url = String(scrubPreferenceCenterUrl(event.request.url));
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.data?.url) {
        breadcrumb.data.url = scrubPreferenceCenterUrl(breadcrumb.data.url);
      }
      if (breadcrumb.data?.from)
        breadcrumb.data.from = scrubPreferenceCenterUrl(breadcrumb.data.from);
      if (breadcrumb.data?.to)
        breadcrumb.data.to = scrubPreferenceCenterUrl(breadcrumb.data.to);
      return breadcrumb;
    },
  });

  console.log(`[Sentry] Initialized for ${environment}`);
}

// Helper to set user context after login
export function setSentryUser(user: {
  id: string;
  email: string;
  role?: string;
}) {
  Sentry.setUser({
    id: user.id,
    email: user.email,
    role: user.role,
  });
}

// Helper to clear user context on logout
export function clearSentryUser() {
  Sentry.setUser(null);
}

// Helper to capture exceptions with context
export function captureException(
  error: Error,
  context?: Record<string, unknown>,
) {
  Sentry.captureException(error, {
    extra: context,
  });
}

// Helper to add breadcrumb for debugging
export function addBreadcrumb(
  message: string,
  category: string,
  data?: Record<string, unknown>,
) {
  Sentry.addBreadcrumb({
    message,
    category,
    data,
    level: "info",
  });
}

// Export Sentry's ErrorBoundary for use in components
export { ErrorBoundary } from "@sentry/react";
